// Widget Action Execution Service
// import api from '../../api'; // TODO: Fix missing api module
import tagService from './tagService';

class WidgetActionService {
  constructor() {
    this.companyId = 1; // Default company ID - should be from user context
  }

  /**
   * Execute a widget action (workflow, code component, pipeline, or custom C#)
   * @param {Object} actionConfig - The action configuration from widget metadata
   * @param {Object} context - Execution context including widget inputs, event data, etc.
   * @returns {Promise<Object>} Execution result
   */
  async executeAction(actionConfig, context = {}) {
    try {

      // Determine action type and prepare appropriate request
      const request = await this.prepareActionRequest(actionConfig, context);
      

      // Call the backend endpoint
      // TODO: Fix missing api module
      // const response = await api.post('/SCADA/execute-widget-action', request);
      // if (response.data.success) {
      //   await this.handleActionOutputs(response.data.outputs, actionConfig);
      //   return {
      //     success: true,
      //     outputs: response.data.outputs,
      //     executionTime: response.data.executionTime,
      //     pipelineResults: response.data.pipelineResults
      //   };
      // } else {
      //   console.error('Widget action execution failed:', response.data.errors);
      //   return {
      //     success: false,
      //     errors: response.data.errors
      //   };
      // }

      throw new Error('Widget actions are not yet implemented - api module missing');
    } catch (error) {
      console.error('Error executing widget action:', error);
      return {
        success: false,
        errors: [error.message || 'Unknown error occurred']
      };
    }
  }

  /**
   * Prepare request payload based on action type
   * @param {Object} actionConfig - Action configuration
   * @param {Object} context - Execution context
   * @returns {Promise<Object>} Request payload
   */
  async prepareActionRequest(actionConfig, context) {
    // Get current tag values that might be needed for the action
    const tagValues = await this.getCurrentTagValues(actionConfig.referencedTags || []);
    
    const baseRequest = {
      widgetId: context.widgetId || 'unknown',
      companyId: this.companyId,
      inputs: context.inputs || {},
      variables: context.variables || {},
      eventValue: context.eventValue,
      tagValues: tagValues
    };

    // Handle different action types
    switch (actionConfig.actionType?.toLowerCase()) {
      case 'workflow':
        return {
          ...baseRequest,
          actionType: 'workflow',
          workflowName: actionConfig.workflowName,
          parameters: actionConfig.parameters || {}
        };

      case 'code':
        return {
          ...baseRequest,
          actionType: 'code',
          componentName: actionConfig.componentName,
          method: actionConfig.method || 'Execute',
          parameters: actionConfig.parameters || {}
        };

      case 'pipeline':
        return {
          ...baseRequest,
          actionType: 'pipeline',
          pipeline: actionConfig.pipeline || []
        };

      default:
        throw new Error(`Unsupported action type: ${actionConfig.actionType}. Supported types: workflow, code, pipeline`);
    }
  }

  /**
   * Get current values for tags referenced in the action
   * @param {Array} tagIds - Array of tag IDs
   * @returns {Promise<Object>} Tag values mapped by tag ID
   */
  async getCurrentTagValues(tagIds) {
    const tagValues = {};
    
    if (!tagIds || tagIds.length === 0) {
      return tagValues;
    }

    try {
      for (const tagId of tagIds) {
        const tag = await tagService.getTag(tagId);
        if (tag) {
          tagValues[tagId] = tag.value;
        }
      }
    } catch (error) {
      console.error('Error getting current tag values:', error);
    }

    return tagValues;
  }

  /**
   * Handle outputs from action execution (e.g., tag updates)
   * @param {Object} outputs - Action execution outputs
   * @param {Object} actionConfig - Original action configuration
   */
  async handleActionOutputs(outputs, actionConfig) {
    if (!outputs || typeof outputs !== 'object') {
      return;
    }

    try {
      // Handle tag updates from outputs
      const tagBindings = actionConfig.outputTagBindings || {};
      
      for (const [outputName, tagId] of Object.entries(tagBindings)) {
        if (outputs.hasOwnProperty(outputName) && tagId) {
          await tagService.updateTagValue(tagId, outputs[outputName]);
        }
      }

      // Handle variable updates if needed
      // This could integrate with a variable service in the future
      const variableBindings = actionConfig.outputVariableBindings || {};
      for (const [outputName, variableName] of Object.entries(variableBindings)) {
        if (outputs.hasOwnProperty(outputName)) {
          // Store in session storage for now
          sessionStorage.setItem(`variable_${variableName}`, JSON.stringify(outputs[outputName]));
        }
      }
    } catch (error) {
      console.error('Error handling action outputs:', error);
    }
  }

  /**
   * Parse and prepare widget inputs from current widget state
   * @param {Object} widgetData - Current widget data
   * @param {Object} metadata - Widget metadata defining inputs
   * @returns {Object} Prepared inputs
   */
  prepareWidgetInputs(widgetData, metadata) {
    const inputs = {};
    
    if (!metadata.inputs || !Array.isArray(metadata.inputs)) {
      return inputs;
    }

    for (const inputDef of metadata.inputs) {
      const inputName = inputDef.name;
      let inputValue = inputDef.defaultValue;

      // Try to get value from widget properties
      if (widgetData.properties && widgetData.properties.hasOwnProperty(inputName)) {
        inputValue = widgetData.properties[inputName];
      } else if (widgetData.hasOwnProperty(inputName)) {
        inputValue = widgetData[inputName];
      }

      // Handle tag bindings for inputs
      if (inputDef.tagBinding) {
        // Tag values should be resolved at execution time
        inputs[inputName] = `@tags[${inputDef.tagBinding}]`;
      } else {
        inputs[inputName] = inputValue;
      }
    }

    return inputs;
  }

  /**
   * Get session variables for widget action context
   * @returns {Object} Current session variables
   */
  getSessionVariables() {
    const variables = {};
    
    // Get all variables from session storage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('variable_')) {
        const variableName = key.substring(9); // Remove 'variable_' prefix
        try {
          variables[variableName] = JSON.parse(sessionStorage.getItem(key));
        } catch (error) {
          variables[variableName] = sessionStorage.getItem(key);
        }
      }
    }

    return variables;
  }

  /**
   * Extract referenced tag IDs from action binding expression
   * @param {string} actionBinding - The action binding expression
   * @returns {Array} Array of tag IDs
   */
  extractReferencedTags(actionBinding) {
    if (!actionBinding) return [];
    
    const tagRegex = /@tags\[([^\]]+)\]/g;
    const referencedTags = [];
    let match;
    
    while ((match = tagRegex.exec(actionBinding)) !== null) {
      const tagId = match[1].replace(/['"]/g, ''); // Remove quotes
      if (!referencedTags.includes(tagId)) {
        referencedTags.push(tagId);
      }
    }
    
    return referencedTags;
  }

  /**
   * Create action execution context for a widget event
   * @param {Object} widgetData - Widget configuration data
   * @param {Object} metadata - Widget metadata
   * @param {Object} eventData - Event data (e.g., click event, value change)
   * @returns {Object} Execution context
   */
  createExecutionContext(widgetData, metadata, eventData = {}) {
    return {
      inputs: this.prepareWidgetInputs(widgetData, metadata),
      outputs: {},
      variables: this.getSessionVariables(),
      eventValue: eventData.value || eventData.target?.value || null,
      eventType: eventData.type || 'unknown'
    };
  }
}

export default new WidgetActionService();